import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { useIsLoggedIn } from 'modules/cms/hooks';
import { CmsPagePageLoadable } from 'modules/cms/pages/Page/CmsPagePageLoadable';
import { CmsPostPageLoadable } from 'modules/cms/pages/Post/CmsPostPageLoadable';
import { LoadingPageLoadable } from 'modules/main/pages/Loading/LoadingPageLoadable';
import { NotFoundPageLoadable } from 'modules/main/pages/NotFound/NotFoundPageLoadable';
import { fetchPage } from 'modules/main/store/ducks/contents.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useAmpContext } from 'utils/AmpContext';

type Props = {
  slug: string;
};

export default function CmsContentPageContainer({ slug }: Props) {
  const dispatch = useMainDispatch();
  const isLoggedIn = useIsLoggedIn();
  const location = useLocation<{ subsite?: string }>();

  const amp = useAmpContext();

  const contents = useMainSelector((state) => state.contents);

  // @ts-expect-error TS(2339): Property 'byUrl' does not exist on type 'never'.
  const contentObj = contents.byUrl[slug] || {};

  const content = contentObj.content as CmsApiPage | undefined;
  const pageState = contentObj.state || 'UNFETCHED';

  const hasHomePage = content?.subsite && Boolean(content.subsite.homePage);
  const heroSearch = content?.heroSearch;

  const onlySearchResults = heroSearch?.onlyResults;
  const onlyArticleResults = content?.onlySearchResults && hasHomePage;

  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    dispatch(fetchPage(slug));
  }, [slug, dispatch, isLoggedIn]);

  if (onlySearchResults && !searchParams.get('q')) {
    searchParams.set('q', '');
    return (
      <Navigate
        to={`${location.pathname}?${searchParams.toString()}`}
        replace
      />
    );
  }

  if (onlyArticleResults && !searchParams.get('pq')) {
    searchParams.set('pq', '');
    return (
      <Navigate
        to={`${location.pathname}?${searchParams.toString()}`}
        replace
      />
    );
  }

  if (['FETCHING', 'UNFETCHED'].includes(pageState)) {
    if (amp) {
      // It's an AMP URL, but content is not already available.
      // Meaning it's disabled for that page.
      return <NotFoundPageLoadable />;
    }

    return <LoadingPageLoadable />;
  }

  if (!content || contentObj.type !== 'CMS_PAGE') {
    return <NotFoundPageLoadable />;
  }

  return content.type === 'PAGE' ? (
    <CmsPagePageLoadable content={content} />
  ) : (
    <CmsPostPageLoadable content={content} />
  );
}
